import {
  Button,
  TextInput,
  TextArea,
  DatePicker,
  TimePicker,
  TimePickerVariant,
  DurationPicker,
  AttendeesInput,
  Checkbox,
  CustomSelect,
  NumberInput,
  RadioButton,
  type SelectOption,
} from '@consigli/facade';
import { useGetProjectParticipantsQuery } from '@consigli/hooks';
import { useForm, SubmitHandler, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { FaTimes } from 'react-icons/fa';

import { RecurrencePattern, RecurrenceEndType, getTodayLocalDateString } from '@/util/date';

import { CopyButton, HintBox } from './components';

interface MeetingInvitationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreateMeeting: (meetingData: MeetingFormData) => void;
  loading?: boolean;
  height?: string;
  projectId: number;
}

interface MeetingFormData {
  title: string;
  date: string;
  time: string;
  duration: number;
  attendees: string[];
  description: string;
  location: string;
  isRecurring: boolean;
  recurrencePattern?: RecurrencePattern;
  recurrenceInterval?: number;
  daysOfWeek?: string[];
  recurrenceEndType?: RecurrenceEndType;
  recurrenceEndDate?: string;
  numberOfOccurrences?: number;
}

export const MeetingInvitationModal = ({
  isOpen,
  onClose,
  onCreateMeeting,
  loading = false,
  height = '80vh',
  projectId,
}: MeetingInvitationModalProps) => {
  const { t } = useTranslation();

  const outlookHashtag = `#ma_${projectId}`;

  const { data: participants } = useGetProjectParticipantsQuery({ projectId }, { skip: !isOpen });
  const participantSuggestions = (participants ?? []).map((p) => ({
    email: p.email,
    name: p.name ?? null,
  }));

  const RECURRENCE_PATTERN_OPTIONS: SelectOption[] = [
    { value: 'daily', label: t('meeting-secretary.meetings.invitation-modal.pattern-daily') },
    { value: 'weekly', label: t('meeting-secretary.meetings.invitation-modal.pattern-weekly') },
    { value: 'monthly', label: t('meeting-secretary.meetings.invitation-modal.pattern-monthly') },
  ];

  const DAYS_OF_WEEK = [
    { value: 'monday', label: t('meeting-secretary.meetings.invitation-modal.day-monday') },
    { value: 'tuesday', label: t('meeting-secretary.meetings.invitation-modal.day-tuesday') },
    { value: 'wednesday', label: t('meeting-secretary.meetings.invitation-modal.day-wednesday') },
    { value: 'thursday', label: t('meeting-secretary.meetings.invitation-modal.day-thursday') },
    { value: 'friday', label: t('meeting-secretary.meetings.invitation-modal.day-friday') },
    { value: 'saturday', label: t('meeting-secretary.meetings.invitation-modal.day-saturday') },
    { value: 'sunday', label: t('meeting-secretary.meetings.invitation-modal.day-sunday') },
  ];

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    control,
    formState: { errors },
    reset,
  } = useForm<MeetingFormData>({
    defaultValues: {
      title: '',
      date: '',
      time: '09:00',
      duration: 30,
      attendees: [],
      description: '',
      location: '',
      isRecurring: false,
      recurrencePattern: RecurrencePattern.Weekly,
      recurrenceInterval: 1,
      daysOfWeek: [],
      recurrenceEndType: RecurrenceEndType.AfterOccurrences,
      recurrenceEndDate: '',
      numberOfOccurrences: 10,
    },
  });

  const watchedAttendees = watch('attendees');
  const isRecurring = watch('isRecurring');
  const recurrencePattern = watch('recurrencePattern');
  const recurrenceEndType = watch('recurrenceEndType');

  const onSubmit: SubmitHandler<MeetingFormData> = (data) => {
    const formDataWithAttendees = {
      ...data,
      attendees: watchedAttendees,
    };

    onCreateMeeting(formDataWithAttendees);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div
      className="fixed inset-0 bg-black/50 z-50"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        zIndex: 9999,
      }}
      onClick={handleOverlayClick}
      onKeyDown={(e) => {
        if (e.key === 'Escape') {
          handleClose();
        }
      }}
      role="button"
      tabIndex={0}
      aria-label="Close modal"
    >
      <div
        className="flex items-center justify-center h-full p-8"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          padding: '32px',
        }}
      >
        {/* Modal content */}
        <div
          className="w-[70vw] max-w-[800px] min-w-[400px] bg-white rounded-lg shadow-xl flex flex-col relative"
          style={{
            maxHeight: height,
            width: '70vw',
            maxWidth: '800px',
            minWidth: '400px',
            backgroundColor: 'white',
            borderRadius: '8px',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
            display: 'flex',
            flexDirection: 'column',
            position: 'relative',
            padding: '16px',
            overflow: 'auto',
          }}
          role="dialog"
          aria-modal="true"
          aria-labelledby="modal-title"
        >
          <div className="flex items-center justify-between p-6 pb-2 flex-shrink-0">
            <div>
              <h2 id="modal-title" className="text-xl font-semibold text-gray-900 mb-1">
                {t('meeting-secretary.meetings.invitation-modal.title')}
              </h2>
              <p className="text-sm text-gray-600">
                {t('meeting-secretary.meetings.invitation-modal.subtitle')}
              </p>
            </div>
            <button
              onClick={handleClose}
              className="p-2 text-gray-400 hover:text-gray-600 transition-colors rounded-full hover:bg-gray-100 cursor-pointer"
              disabled={loading}
            >
              <FaTimes size={18} />
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col flex-1 min-h-0">
            <div className="p-6 space-y-6 overflow-y-auto flex-1">
              {/* Import from Outlook hint */}
              <HintBox
                variant="info"
                title={t('meeting-secretary.meetings.invitation-modal.outlook-hashtag-title')}
              >
                <ul className="text-sm text-gray-700 space-y-1">
                  <li>
                    <span className="font-semibold">
                      {t(
                        'meeting-secretary.meetings.invitation-modal.outlook-hashtag-manual-label',
                      )}
                      :
                    </span>{' '}
                    {t('meeting-secretary.meetings.invitation-modal.outlook-hashtag-manual')}
                  </li>
                  <li>
                    <span className="font-semibold">
                      {t(
                        'meeting-secretary.meetings.invitation-modal.outlook-hashtag-automatic-label',
                      )}
                      :
                    </span>{' '}
                    {t('meeting-secretary.meetings.invitation-modal.outlook-hashtag-automatic')}
                  </li>
                  <li>
                    {t('meeting-secretary.meetings.invitation-modal.outlook-hashtag-recurring')}
                  </li>
                </ul>
                <div className="flex items-center gap-2">
                  <code className="px-2 py-1 bg-white border border-blue-200 rounded text-sm font-mono text-gray-900 select-all">
                    {outlookHashtag}
                  </code>
                  <CopyButton
                    textToCopy={outlookHashtag}
                    label={t('meeting-secretary.meetings.invitation-modal.outlook-hashtag-copy')}
                    copiedLabel={t(
                      'meeting-secretary.meetings.invitation-modal.outlook-hashtag-copied',
                    )}
                    className="flex items-center gap-1.5 px-2 py-1 text-sm text-blue-600 hover:text-blue-800 hover:bg-blue-100 rounded transition-colors cursor-pointer"
                  />
                </div>
              </HintBox>

              {/* Legal Alert */}
              <HintBox>{t('meeting-secretary.meetings.invitation-modal.legal-alert')}</HintBox>

              {/* Subject */}
              <div className="my-6">
                <TextInput
                  label={`${t('meeting-secretary.meetings.invitation-modal.subject-label')} *`}
                  {...register('title', {
                    required: t('meeting-secretary.meetings.invitation-modal.subject-required'),
                    minLength: {
                      value: 2,
                      message: t('meeting-secretary.meetings.invitation-modal.subject-min-length'),
                    },
                  })}
                  placeholder={t('meeting-secretary.meetings.invitation-modal.subject-placeholder')}
                  disabled={loading}
                  className="w-full"
                />
                {errors.title && (
                  <p className="text-red-500 text-xs mt-1">{errors.title.message}</p>
                )}
              </div>

              {/* Date, Time, Duration Row */}
              <div className="flex gap-4 flex-nowrap mb-6">
                {/* Date */}
                <div className="flex-1">
                  <DatePicker
                    label={`${t('meeting-secretary.meetings.invitation-modal.date-label')} *`}
                    {...register('date', {
                      required: t('meeting-secretary.meetings.invitation-modal.date-required'),
                      validate: (value) => {
                        const todayLocal = getTodayLocalDateString();
                        return (
                          value >= todayLocal ||
                          t('meeting-secretary.meetings.invitation-modal.date-past-error')
                        );
                      },
                    })}
                    min={getTodayLocalDateString()}
                    disabled={loading}
                    error={errors.date?.message}
                    className="w-full"
                  />
                </div>

                {/* Time */}
                <div className="flex-1">
                  <TimePicker
                    label={`${t('meeting-secretary.meetings.invitation-modal.time-label')} *`}
                    {...register('time', {
                      required: t('meeting-secretary.meetings.invitation-modal.time-required'),
                    })}
                    variant={TimePickerVariant.Native}
                    disabled={loading}
                    error={errors.time?.message}
                    className="w-full"
                  />
                  {errors.time && (
                    <p className="text-red-500 text-xs mt-1">{errors.time.message}</p>
                  )}
                </div>

                {/* Duration */}
                <div className="flex-1">
                  <DurationPicker
                    label={`${t('meeting-secretary.meetings.invitation-modal.duration-label')} *`}
                    {...register('duration', {
                      required: t('meeting-secretary.meetings.invitation-modal.duration-required'),
                      min: {
                        value: 1,
                        message: t(
                          'meeting-secretary.meetings.invitation-modal.duration-min-error',
                        ),
                      },
                    })}
                    min={1}
                    max={480}
                    disabled={loading}
                    error={errors.duration?.message}
                    className="w-full"
                  />
                  {errors.duration && (
                    <p className="text-red-500 text-xs mt-1">{errors.duration.message}</p>
                  )}
                </div>
              </div>

              {/* Location */}
              <div className="mb-6">
                <TextInput
                  label={t('meeting-secretary.meetings.invitation-modal.location-label')}
                  {...register('location')}
                  placeholder={t(
                    'meeting-secretary.meetings.invitation-modal.location-placeholder',
                  )}
                  disabled={loading}
                  className="w-full"
                />
              </div>

              {/* Attendees */}
              <div>
                <AttendeesInput
                  label={`${t('meeting-secretary.meetings.invitation-modal.attendees-label')} *`}
                  value={watchedAttendees}
                  onChange={(attendees: string[]) => setValue('attendees', attendees)}
                  disabled={loading}
                  placeholder={t(
                    'meeting-secretary.meetings.invitation-modal.attendees-placeholder',
                  )}
                  error={
                    watchedAttendees.length === 0
                      ? t('meeting-secretary.meetings.invitation-modal.attendees-required')
                      : undefined
                  }
                  suggestions={participantSuggestions}
                  className="w-full"
                />
              </div>

              {/* Description */}
              <div>
                <TextArea
                  label={t('meeting-secretary.meetings.invitation-modal.description-label')}
                  {...register('description')}
                  placeholder={t(
                    'meeting-secretary.meetings.invitation-modal.description-placeholder',
                  )}
                  rows={4}
                  disabled={loading}
                  className="w-full resize-vertical"
                />
              </div>

              {/* Recurring Meeting Toggle */}
              <div className="p-4 bg-blue-50 rounded-lg">
                <Controller
                  name="isRecurring"
                  control={control}
                  render={({ field }) => (
                    <Checkbox
                      id="isRecurring"
                      label={t('meeting-secretary.meetings.invitation-modal.recurring-label')}
                      checked={field.value}
                      onChange={(e) => field.onChange(e.target.checked)}
                      disabled={loading}
                    />
                  )}
                />
                <p className="text-xs text-gray-600 mt-1 ml-10">
                  {t('meeting-secretary.meetings.invitation-modal.recurring-description')}
                </p>
              </div>

              {/* Recurring Meeting Options */}
              {isRecurring && (
                <div className="space-y-4 p-4 border border-gray-200 rounded-lg bg-gray-50">
                  <h3 className="text-sm font-semibold text-gray-900">
                    {t('meeting-secretary.meetings.invitation-modal.recurrence-settings')}
                  </h3>

                  {/* Recurrence Pattern and Interval */}
                  <div className="flex gap-4">
                    <div className="flex-1">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('meeting-secretary.meetings.invitation-modal.repeat-label')} *
                      </label>
                      <Controller
                        name="recurrencePattern"
                        control={control}
                        rules={{
                          required: isRecurring
                            ? t(
                                'meeting-secretary.meetings.invitation-modal.repeat-pattern-required',
                              )
                            : false,
                        }}
                        render={({ field }) => (
                          <CustomSelect
                            options={RECURRENCE_PATTERN_OPTIONS}
                            value={
                              field.value
                                ? [
                                    RECURRENCE_PATTERN_OPTIONS.find(
                                      (opt) => opt.value === field.value,
                                    )!,
                                  ]
                                : undefined
                            }
                            onChange={(options) => field.onChange(options[0]?.value)}
                            className="w-full"
                          />
                        )}
                      />
                      {errors.recurrencePattern && (
                        <p className="text-red-500 text-xs mt-1">
                          {errors.recurrencePattern.message}
                        </p>
                      )}
                    </div>

                    <div className="flex-1">
                      <NumberInput
                        label={`${t('meeting-secretary.meetings.invitation-modal.interval-label')} *`}
                        {...register('recurrenceInterval', {
                          required: isRecurring
                            ? t('meeting-secretary.meetings.invitation-modal.interval-required')
                            : false,
                          min: {
                            value: 1,
                            message: t(
                              'meeting-secretary.meetings.invitation-modal.interval-min-error',
                            ),
                          },
                        })}
                        min={1}
                        max={30}
                        disabled={loading}
                        className="w-full"
                      />
                      {errors.recurrenceInterval && (
                        <p className="text-red-500 text-xs mt-1">
                          {errors.recurrenceInterval.message}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Days of Week (for weekly pattern) */}
                  {recurrencePattern === RecurrencePattern.Weekly && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('meeting-secretary.meetings.invitation-modal.repeat-on-label')} *
                      </label>
                      <Controller
                        name="daysOfWeek"
                        control={control}
                        rules={{
                          required:
                            isRecurring && recurrencePattern === RecurrencePattern.Weekly
                              ? t('meeting-secretary.meetings.invitation-modal.repeat-on-required')
                              : false,
                        }}
                        render={({ field }) => (
                          <div className="flex flex-wrap gap-3">
                            {DAYS_OF_WEEK.map((day) => (
                              <Checkbox
                                key={day.value}
                                id={`day-${day.value}`}
                                label={day.label}
                                checked={(field.value || []).includes(day.value)}
                                onChange={(e) => {
                                  const currentValues = field.value || [];
                                  if (e.target.checked) {
                                    field.onChange([...currentValues, day.value]);
                                  } else {
                                    field.onChange(
                                      currentValues.filter((v: string) => v !== day.value),
                                    );
                                  }
                                }}
                                disabled={loading}
                              />
                            ))}
                          </div>
                        )}
                      />
                      {errors.daysOfWeek && (
                        <p className="text-red-500 text-xs mt-1">{errors.daysOfWeek.message}</p>
                      )}
                    </div>
                  )}

                  {/* Recurrence End */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('meeting-secretary.meetings.invitation-modal.ends-label')} *
                    </label>
                    <div className="space-y-3">
                      {/* By Date */}
                      <div className="flex items-center gap-2">
                        <Controller
                          name="recurrenceEndType"
                          control={control}
                          render={({ field }) => (
                            <RadioButton
                              label={t('meeting-secretary.meetings.invitation-modal.ends-on-label')}
                              name="recurrenceEndType"
                              value={RecurrenceEndType.ByDate}
                              checked={field.value === RecurrenceEndType.ByDate}
                              onChange={() => field.onChange(RecurrenceEndType.ByDate)}
                              disabled={loading}
                            />
                          )}
                        />
                        <DatePicker
                          {...register('recurrenceEndDate', {
                            required:
                              isRecurring && recurrenceEndType === RecurrenceEndType.ByDate
                                ? t('meeting-secretary.meetings.invitation-modal.ends-on-required')
                                : false,
                          })}
                          min={watch('date') || getTodayLocalDateString()}
                          disabled={loading || recurrenceEndType !== RecurrenceEndType.ByDate}
                          error={errors.recurrenceEndDate?.message}
                          className="flex-1"
                        />
                      </div>

                      {/* After Occurrences */}
                      <div className="flex items-start gap-2">
                        <div className="pt-2">
                          <Controller
                            name="recurrenceEndType"
                            control={control}
                            render={({ field }) => (
                              <RadioButton
                                label={t(
                                  'meeting-secretary.meetings.invitation-modal.ends-after-label',
                                )}
                                name="recurrenceEndType"
                                value={RecurrenceEndType.AfterOccurrences}
                                checked={field.value === RecurrenceEndType.AfterOccurrences}
                                onChange={() => field.onChange(RecurrenceEndType.AfterOccurrences)}
                                disabled={loading}
                              />
                            )}
                          />
                        </div>
                        <div className="flex items-center gap-2 flex-1">
                          <NumberInput
                            {...register('numberOfOccurrences', {
                              required:
                                isRecurring &&
                                recurrenceEndType === RecurrenceEndType.AfterOccurrences
                                  ? t(
                                      'meeting-secretary.meetings.invitation-modal.ends-after-required',
                                    )
                                  : false,
                              min: {
                                value: 1,
                                message: t(
                                  'meeting-secretary.meetings.invitation-modal.ends-after-min-error',
                                ),
                              },
                            })}
                            min={1}
                            max={100}
                            disabled={
                              loading || recurrenceEndType !== RecurrenceEndType.AfterOccurrences
                            }
                            className="w-24"
                          />
                          <span className="text-sm text-gray-900">
                            {t(
                              'meeting-secretary.meetings.invitation-modal.ends-after-occurrences',
                            )}
                          </span>
                        </div>
                        {errors.numberOfOccurrences && (
                          <p className="text-red-500 text-xs mt-1">
                            {errors.numberOfOccurrences.message}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-lg flex-shrink-0">
              <Button secondary onClick={handleClose} rounded disabled={loading} type="button">
                {t('meeting-secretary.meetings.invitation-modal.cancel-button')}
              </Button>
              <Button
                primary
                rounded
                disabled={loading || watchedAttendees.length === 0}
                type="submit"
                onClick={() =>
                  console.log('Schedule Meeting button clicked, attendees:', watchedAttendees)
                }
              >
                {loading
                  ? t('meeting-secretary.meetings.invitation-modal.submitting-button')
                  : t('meeting-secretary.meetings.invitation-modal.submit-button')}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
